import { useEffect, useMemo, useState } from 'react';
import { Camera, ImageIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useFotosChipConversa, type ChipLidoDaFoto, type LinhaAtivacao } from '@/hooks/useAtivacaoLinhas';
import { iccidFecha, linhaEstaValidada } from './ativacaoResumo';

const iccidValido = (v: string) => /^89\d{17,18}$/.test(v.replace(/[\s-]/g, ''));
const telefoneTela = (t: string) => t.replace(/^55(?=\d{10,11}$)/, '').replace(/^(\d{2})(\d{4,5})(\d{4})$/, '($1) $2-$3');
const dataCurta = (v: string) => new Date(v).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });

interface Rascunho { foto: string; iccid: string }

/**
 * ⛔ A CONFERÊNCIA DO CHIP FÍSICO É DO PEDIDO, NÃO DA LINHA (Douglas 16/10/2026,
 * maquete aprovada). Caso MB ADMINISTRADORA: 4 linhas, e a consultora escreveu
 * os 4 ICCIDs numa nota da linha do tempo porque conferir era abrir linha por
 * linha, escolher a foto numa grade sem saber o chip de cada uma e salvar 4
 * vezes. Em 30 dias, 224 notas no Inbox com ICCID escrito à mão. Aqui o pedido
 * inteiro numa tabela: as fotos com o ICCID que a leitura já viu, uma linha por
 * número, um botão que salva todas. A troca à gerente continua saindo sozinha
 * pela automação depois do ICCID conferido; as portas são as mesmas da linha
 * (corrigir: substituir_foto e conferir_iccid).
 */
export function ConferenciaChipsPedido({ linhas, chipsLidos, conversaId, ocupado, executar, avisar }: {
  linhas: LinhaAtivacao[]; chipsLidos: ChipLidoDaFoto[]; conversaId: string; ocupado: boolean;
  executar: (path: string, body: unknown) => Promise<unknown>;
  avisar: (texto: string, erro?: boolean) => void;
}) {
  const chips = useMemo(() => linhas.filter(l => l.tipo === 'esim_simcard' && !linhaEstaValidada(l)), [linhas]);
  const fotosConversa = useFotosChipConversa(conversaId, !!conversaId && chips.length > 0);
  const [rascunho, setRascunho] = useState<Record<string, Rascunho>>({});
  const [conferi, setConferi] = useState(false);
  const [confirmarFoto, setConfirmarFoto] = useState(false);
  const [salvando, setSalvando] = useState(false);

  const lidos = useMemo(() => new Map(chipsLidos.map(f => [f.mensagem_id, f])), [chipsLidos]);
  const fotos = useMemo(() => {
    const vistas = new Map<string, { id: string; quando: string; url: string | null; chips: string[] }>();
    for (const f of fotosConversa.data?.pages.flat() || []) {
      vistas.set(f.id, { id: f.id, quando: f.created_at, url: f.media_url, chips: (lidos.get(f.id)?.chips || []).map(c => c.iccid) });
    }
    for (const f of chipsLidos) if (!vistas.has(f.mensagem_id)) {
      vistas.set(f.mensagem_id, { id: f.mensagem_id, quando: f.criado_em, url: f.foto_url, chips: f.chips.map(c => c.iccid) });
    }
    return [...vistas.values()].slice(0, 8);
  }, [fotosConversa.data, chipsLidos, lidos]);

  const iccidsEmUso = new Set(chips.filter(l => l.conferido_em && l.iccid).map(l => l.iccid as string));
  useEffect(() => {
    setRascunho(atual => {
      const novo = { ...atual };
      for (const l of chips) {
        if (novo[l.id]) continue;
        const anotado = chipsLidos.flatMap(f => f.chips.map(c => ({ ...c, foto: f.mensagem_id })))
          .find(c => c.linhaAnotada && l.telefone.replace(/\D/g, '').endsWith(c.linhaAnotada));
        const foto = l.foto_mensagem_id || anotado?.foto || '';
        const daFoto = lidos.get(foto)?.chips.filter(c => !iccidsEmUso.has(c.iccid)) || [];
        novo[l.id] = { foto, iccid: l.iccid || anotado?.iccid || (daFoto.length === 1 ? daFoto[0].iccid : '') };
      }
      return novo;
    });
  }, [chips, chipsLidos]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!chips.length || chips.every(l => l.conferido_em && l.iccid)) return null;

  const situacao = (l: LinhaAtivacao) => {
    const r = rascunho[l.id];
    if (l.conferido_em && l.iccid && r?.iccid === l.iccid && r?.foto === l.foto_mensagem_id) return 'conferido';
    if (!r?.foto) return fotos.length ? 'escolher foto' : 'sem foto';
    return 'conferir';
  };
  const prontas = chips.filter(l => situacao(l) === 'conferir' && iccidValido(rascunho[l.id]?.iccid || ''));
  // O pedido da foto vai às linhas que o servidor vê sem chip: sem foto salva e sem ICCID.
  const semFoto = chips.filter(l => !l.foto_mensagem_id && !l.iccid);
  const repetidos = (() => {
    const vistos = new Map<string, number>();
    for (const l of chips) { const i = (rascunho[l.id]?.iccid || '').replace(/\D/g, ''); if (i) vistos.set(i, (vistos.get(i) || 0) + 1); }
    return new Set([...vistos].filter(([, n]) => n > 1).map(([i]) => i));
  })();
  const ocupada = ocupado || salvando;

  async function salvar() {
    if (!conferi) { avisar('Marque que conferiu cada ICCID na foto antes de salvar.', true); return; }
    if (prontas.some(l => repetidos.has(rascunho[l.id].iccid.replace(/\D/g, '')))) { avisar('O mesmo ICCID está em duas linhas. Cada chip é de uma linha só.', true); return; }
    setSalvando(true);
    let feitas = 0;
    try {
      for (const l of prontas) {
        const r = rascunho[l.id];
        let revisao = l.revisao;
        const motivo = `Conferência por pedido: chip da linha ${l.telefone} conferido na foto`;
        if (r.foto !== l.foto_mensagem_id) {
          const vinculo = await executar(`linhas/${l.id}/corrigir`, { revisao, motivo, correcao: { acao: 'substituir_foto', mensagemId: r.foto } }) as { revisao?: number } | false;
          if (!vinculo) return;
          revisao = vinculo.revisao ?? revisao;
        }
        const conferida = await executar(`linhas/${l.id}/corrigir`, { revisao, motivo, correcao: { acao: 'conferir_iccid', iccid: r.iccid.replace(/[\s-]/g, '') } });
        if (!conferida) return;
        feitas++;
      }
      setConferi(false);
      avisar(`${feitas === 1 ? '1 linha conferida' : `${feitas} linhas conferidas`}. A troca à gerente sai sozinha em até 5 minutos, e o cliente é avisado por aqui.`);
    } finally { setSalvando(false); }
  }

  async function pedirFoto() {
    const alvo = semFoto.find(l => l.entrada_em);
    if (!alvo) { avisar('As linhas sem foto ainda não ativaram na operadora: o pedido da foto sai quando ativarem.', true); return; }
    if (!confirmarFoto) { setConfirmarFoto(true); avisar(`Pedir ao cliente a foto do chip ${semFoto.length === 1 ? 'da linha sem foto' : `das ${semFoto.length} linhas sem foto`}? Clique de novo para confirmar.`); return; }
    setConfirmarFoto(false);
    const ok = await executar(`linhas/${alvo.id}/solicitar-confirmacao`, { revisao: alvo.revisao, conversaId, pedirFoto: true });
    if (ok) avisar('Pedido da foto enviado ao cliente.');
  }

  return <div className="overflow-hidden rounded-xl border border-border-warm bg-card">
    <div className="flex h-[30px] items-center justify-between gap-2 bg-[#FAF8F4] px-3 dark:bg-[#241A1A]">
      <span className="truncate text-[10px] font-bold uppercase tracking-[1.2px] text-muted-foreground">Chip físico · o pedido inteiro</span>
      <span className="shrink-0 text-[10.5px] text-muted-foreground">{chips.length === 1 ? '1 linha' : `${chips.length} linhas`}</span>
    </div>
    <div className="space-y-2 px-3 py-2.5">
      <p className="text-[9px] font-bold uppercase tracking-wider text-muted-foreground">Fotos de chip na conversa</p>
      {!fotos.length && <p className="text-[11px] text-muted-foreground">Nenhuma foto recebida do cliente nesta conversa.</p>}
      {!!fotos.length && <div className="flex flex-wrap gap-2">{fotos.map(f => <a key={f.id} href={f.url || undefined} target="_blank" rel="noopener noreferrer"
        className="flex min-w-0 items-center gap-1.5 rounded-lg border border-border-warm px-1.5 py-1 hover:bg-[#F4F0EA] dark:hover:bg-[#2A1F1F]" title="Abrir a foto">
        {f.url ? <img src={f.url} alt={`Foto do cliente de ${dataCurta(f.quando)}`} className="h-8 w-11 shrink-0 rounded object-cover" />
          : <span className="flex h-8 w-11 shrink-0 items-center justify-center rounded bg-muted text-muted-foreground"><ImageIcon className="h-3.5 w-3.5" aria-hidden="true" /></span>}
        <span className="min-w-0 text-[10px] leading-tight">
          <span className="block text-muted-foreground">{dataCurta(f.quando)}</span>
          <span className={`block truncate font-mono ${f.chips.length ? '' : 'text-primary'}`}>{f.chips.length ? f.chips.map(i => `…${i.slice(-6)}`).join(' ') : 'não leu'}</span>
        </span>
      </a>)}</div>}
    </div>
    <div className="border-t border-border-warm">
      <div className="grid h-7 grid-cols-[minmax(0,1fr)_76px] items-center gap-2 px-3 text-[8.5px] font-bold uppercase tracking-[1.1px] text-muted-foreground">
        <span>Linha · foto · ICCID</span><span className="text-right">Situação</span>
      </div>
      {chips.map((l, n) => {
        const r = rascunho[l.id] || { foto: '', iccid: '' };
        const s = situacao(l);
        const limpo = r.iccid.replace(/\D/g, '');
        return <div key={l.id} className={`grid grid-cols-[minmax(0,1fr)_76px] items-start gap-2 border-t border-border-warm px-3 py-2 ${n % 2 ? 'bg-[#FBF9F6] dark:bg-[#241A1A]' : ''}`}>
          <div className="min-w-0 space-y-1.5">
            <p className="truncate text-[12.5px] font-semibold tabular-nums" title={l.telefone}>{telefoneTela(l.telefone)}</p>
            <div className="flex min-w-0 gap-1.5">
              <select aria-label={`Foto do chip da linha ${l.telefone}`} disabled={ocupada || !fotos.length} value={r.foto}
                className="h-[29px] w-[118px] shrink-0 rounded-md border border-input bg-background px-1.5 text-[11px]"
                onChange={e => {
                  const foto = e.target.value;
                  const daFoto = lidos.get(foto)?.chips.filter(c => !iccidsEmUso.has(c.iccid)) || [];
                  setRascunho({ ...rascunho, [l.id]: { foto, iccid: daFoto.length === 1 ? daFoto[0].iccid : r.iccid } });
                }}>
                <option value="">Sem foto</option>
                {fotos.map(f => <option key={f.id} value={f.id}>{dataCurta(f.quando)}{f.chips.length ? ` · …${f.chips[0].slice(-4)}` : ''}</option>)}
              </select>
              <Input aria-label={`ICCID da linha ${l.telefone}`} className="h-[29px] min-w-0 flex-1 font-mono text-[11px]" inputMode="numeric" maxLength={40}
                disabled={ocupada || !r.foto} placeholder={r.foto ? '89… (20 dígitos)' : 'escolha a foto'} value={r.iccid}
                onChange={e => setRascunho({ ...rascunho, [l.id]: { ...r, iccid: e.target.value } })} />
            </div>
            {limpo.length >= 19 && !iccidFecha(limpo) && <p className="text-[10px] text-primary">Este número não fecha no dígito verificador do chip. Confira na etiqueta.</p>}
            {repetidos.has(limpo) && <p className="text-[10px] text-primary">Este ICCID está em outra linha.</p>}
          </div>
          <span className={`mt-0.5 justify-self-end whitespace-nowrap rounded-full border px-2 py-0.5 text-[10px] ${s === 'conferido'
            ? 'border-[#075E54] text-[#075E54] dark:border-[#25D366] dark:text-[#25D366]' : 'border-primary text-primary'}`}>{s}</span>
        </div>;
      })}
    </div>
    <div className="space-y-2 border-t border-border-warm px-3 py-2.5">
      <label className="flex items-center gap-2 text-[11px]"><input type="checkbox" checked={conferi} disabled={ocupada} onChange={e => setConferi(e.target.checked)} />Conferi cada ICCID na foto, linha por linha</label>
      <div className="flex flex-nowrap items-center gap-2">
        <Button size="sm" className="h-[29px] shrink-0 whitespace-nowrap px-2 text-[11px]" disabled={ocupada || !prontas.length} onClick={() => void salvar()}>
          {prontas.length > 1 ? `Salvar os ${prontas.length} e pedir a troca à gerente` : 'Salvar e pedir a troca à gerente'}
        </Button>
        {!!semFoto.length && <Button variant="outline" size="sm" className="h-[29px] min-w-0 gap-1.5 overflow-hidden whitespace-nowrap px-2 text-[11px]" disabled={ocupada || !conversaId} onClick={() => void pedirFoto()}>
          <Camera className="h-3.5 w-3.5 shrink-0" aria-hidden="true" /><span className="truncate">{confirmarFoto ? 'Confirmar o pedido da foto' : semFoto.length === 1 ? 'Pedir ao cliente a foto da linha sem foto' : `Pedir ao cliente a foto das ${semFoto.length} linhas sem foto`}</span>
        </Button>}
      </div>
      <p className="text-[10px] text-muted-foreground">A troca sai pelo CRM, com a foto de cada chip. O cliente não manda e-mail à Claro.</p>
    </div>
  </div>;
}
